
import { useRef, type KeyboardEvent, type ReactNode } from "react";

import styles from "./shell.module.css";

export type Choice<T extends string> = { value: T; label: string; icon?: ReactNode };

type Props<T extends string> = {
  label: string;
  value: T;
  choices: Choice<T>[];
  onChange: (value: T) => void;
};

const NEXT_KEYS = new Set(["ArrowRight", "ArrowDown"]);
const PREVIOUS_KEYS = new Set(["ArrowLeft", "ArrowUp"]);

export function ChoiceGroup<T extends string>({ label, value, choices, onChange }: Props<T>) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const selectedIndex = Math.max(0, choices.findIndex((choice) => choice.value === value));

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = NEXT_KEYS.has(event.key) ? 1 : PREVIOUS_KEYS.has(event.key) ? -1 : 0;
    if (step === 0) return;
    event.preventDefault();
    const next = (selectedIndex + step + choices.length) % choices.length;
    onChange(choices[next].value);
    refs.current[next]?.focus();
  };

  return (
    <div className={styles.choiceRow}>
      <span className={styles.choiceLabel} aria-hidden="true">{label}</span>
      <div role="radiogroup" aria-label={label} className={styles.choiceGroup} onKeyDown={onKeyDown}>
        {choices.map((choice, index) => {
          const checked = index === selectedIndex;
          return (
            <button
              key={choice.value}
              ref={(node) => {
                refs.current[index] = node;
              }}
              type="button"
              role="radio"
              aria-checked={checked}
              tabIndex={checked ? 0 : -1}
              className={styles.choice}
              onClick={() => onChange(choice.value)}
            >
              {choice.icon}
              <span>{choice.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
